import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  HandHeart,
  Home,
  BookOpen,
  Menu,
  HeartHandshake,
  Images,
  Info,
  Mail,
  MapPin,
  GraduationCap,
  Users,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ofuesiLogo from '@/assets/ofuesi-logo.png';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { MOBILE_TABS, getActiveTab, shouldShowMobileShell, type MobileTabId } from './mobileShellRoutes';

const TAB_ICONS: Record<MobileTabId, typeof Home> = {
  home: Home,
  conferences: CalendarDays,
  prayer: HandHeart,
  resources: BookOpen,
  more: Menu,
};

const MORE_LINKS = [
  { to: '/about', label: 'About OFUESI', icon: Info },
  { to: '/regions', label: 'Regions', icon: MapPin },
  { to: '/new-students', label: 'New Students', icon: GraduationCap },
  { to: '/womens-ministry', label: "Women's Ministry", icon: Users },
  { to: '/gallery', label: 'Gallery', icon: Images },
  { to: '/give', label: 'Give', icon: HeartHandshake },
  { to: '/contact', label: 'Contact', icon: Mail },
];

/**
 * Mobile-only application shell: a compact branded header and a bottom tab bar.
 * Hidden at desktop widths (`md:`), when printing, and on admin/document routes.
 */
const MobileAppShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!shouldShowMobileShell(location.pathname)) return null;

  const activeTab = getActiveTab(location.pathname);

  const go = (to: string) => {
    setMoreOpen(false);
    navigate(to);
  };

  return (
    <div data-testid="mobile-app-shell" className="md:hidden print:hidden">
      {/* Compact branded header */}
      <header
        className="fixed inset-x-0 top-0 z-40 border-b border-white/10 bg-primary text-primary-foreground shadow-sm"
        style={{ paddingTop: 'var(--safe-area-top, 0px)' }}
      >
        <div className="flex h-14 items-center gap-3 px-4">
          <Link to="/" aria-label="OFUESI home" className="flex items-center gap-2.5 min-w-0">
            <img src={ofuesiLogo} alt="OFUESI logo" className="h-10 w-10 flex-shrink-0 object-contain" />
            <span className="min-w-0">
              <span className="block text-base font-semibold leading-tight tracking-wide">OFUESI</span>
              <span className="block truncate text-[10px] leading-tight text-secondary">
                Overseas Friends of UESI
              </span>
            </span>
          </Link>
        </div>
      </header>
      {/* Bottom tab bar */}
      <nav
        aria-label="Primary"
        className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85"
        style={{ paddingBottom: 'var(--safe-area-bottom, 0px)' }}
      >
        <ul className="grid grid-cols-5">
          {MOBILE_TABS.map((tab) => {
            const Icon = TAB_ICONS[tab.id];
            const isActive = activeTab === tab.id;
            const content = (
              <>
                <span
                  className={cn(
                    'flex h-7 w-12 items-center justify-center rounded-full transition-colors',
                    isActive ? 'bg-secondary/25 text-primary' : 'text-muted-foreground',
                  )}
                >
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <span
                  className={cn(
                    'text-[11px] font-medium leading-none',
                    isActive ? 'text-primary' : 'text-muted-foreground',
                  )}
                >
                  {tab.label}
                </span>
              </>
            );

            const classes =
              'flex h-16 w-full flex-col items-center justify-center gap-1 px-1 outline-none focus-visible:ring-2 focus-visible:ring-ring';

            return (
              <li key={tab.id}>
                {tab.to ? (
                  <Link
                    to={tab.to}
                    aria-label={tab.label}
                    aria-current={isActive ? 'page' : undefined}
                    className={classes}
                  >
                    {content}
                  </Link>
                ) : (
                  <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
                    <SheetTrigger aria-label="More pages" aria-expanded={moreOpen} className={classes}>
                      {content}
                    </SheetTrigger>
                    <SheetContent side="bottom" className="rounded-t-2xl pb-8">
                      <SheetHeader className="text-left">
                        <SheetTitle className="text-primary">More from OFUESI</SheetTitle>
                      </SheetHeader>
                      <ul className="mt-4 grid gap-1">
                        {MORE_LINKS.map(({ to, label, icon: LinkIcon }) => (
                          <li key={to}>
                            <button
                              type="button"
                              onClick={() => go(to)}
                              className="flex w-full min-h-12 items-center gap-3 rounded-xl px-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted"
                            >
                              <LinkIcon className="h-5 w-5 text-secondary" aria-hidden="true" />
                              {label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </SheetContent>
                  </Sheet>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default MobileAppShell;
